"use client";

import { motion } from "motion/react";
import Link from "next/link";
import Image from "next/image";
import { Star, MapPin, ArrowRight, Navigation } from "lucide-react";
import { useAppSelector } from "@/_hooks/hooks";
import { isValidImgSrc, cleanHeaderTags } from "@/_helper/helper";

export default function HotelsClient({ HotelList }: { HotelList: any[] }) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === "sou-hotels") ||
    {};

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[50vh] overflow-hidden">
          {isValidImgSrc(banner?.BannerImage) && (
            <Image
              src={banner.BannerImage}
              alt={banner?.BannerTitle || "Banner Image"}
              fill
              className="object-cover"
              priority
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

          <div className="absolute inset-0 flex items-end pointer-events-none">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-5xl md:text-7xl font-bold text-amber-300 mb-6 drop-shadow-2xl">
                  {banner?.BannerTitle || "Premium Hotels"}
                </h1>
                {banner?.Description && (
                  <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto drop-shadow-lg mb-6">
                    {banner?.Description}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Hotels Grid */}
        {HotelList && HotelList?.length > 0 && (
          <section className="py-16 bg-gradient-to-b from-white to-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {HotelList?.map((hotel: any, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="h-full flex flex-col"
                  >
                    <Link
                      href={hotel?.URL ? `/sou-hotels/${hotel?.URL}` : "#"}
                      className="block h-full"
                    >
                      <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 group h-full flex flex-col">
                        {/* Image */}
                        <div className="relative h-64 overflow-hidden shrink-0">
                          <Image
                            src={
                              isValidImgSrc(hotel?.MainImage)
                                ? hotel.MainImage
                                : "/placeholder.jpg"
                            }
                            alt={hotel?.HotelName || "Hotel Image"}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          {hotel?.CityName && (
                            <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 pb-1 rounded-full shadow-lg">
                              <span className="text-orange-600 font-bold text-xs">
                                {hotel?.CityName}
                              </span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                        </div>

                        {/* Content */}
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {hotel?.HotelName}
                          </h3>

                          {hotel?.HotelCategory && (
                            <div className="flex items-center gap-4 mb-3">
                              <div className="flex items-center gap-1">
                                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                                <span className="font-bold text-gray-900">
                                  {hotel?.HotelCategory} Star
                                </span>
                              </div>
                              {/* <span className="text-gray-600 text-sm">
                          ({hotel?.reviews} reviews)
                        </span> */}
                            </div>
                          )}

                          <div className="flex flex-col gap-1.5 mb-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <MapPin className="w-4 h-4 text-orange-600 shrink-0" />
                              <span className="text-sm">{hotel?.Address}</span>
                            </div>
                            {hotel?.FarFrom && +hotel?.FarFrom > 0 && (
                              <div className="flex items-center gap-2 text-orange-600 font-medium">
                                <Navigation className="w-4 h-4 shrink-0 fill-orange-600/10 " />
                                <span className="text-sm">
                                  {hotel?.FarFrom} km away from SOU
                                </span>
                              </div>
                            )}
                          </div>

                          <p
                            className="text-gray-600 mb-4 line-clamp-2 min-h-[3rem] blog-content"
                            dangerouslySetInnerHTML={{
                              __html: cleanHeaderTags(hotel?.ShortDescription),
                            }}
                          />

                          {/* Amenities */}
                          {/* <div className="flex flex-wrap gap-2 mb-4">
                        {hotel?.amenities.slice(0, 4).map((amenity, i) => (
                          <span
                            key={i}
                            className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div> */}
                          {hotel?.Facilities &&
                            hotel?.Facilities?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-auto mb-4">
                                {hotel?.Facilities?.map(
                                  (amenity: any, i: number) => {
                                    return (
                                      <div
                                        key={i}
                                        className="flex items-center gap-1 px-2 py-1 bg-gradient-to-br from-orange-50 to-amber-50 text-orange-700 rounded-lg text-[10px] font-medium border border-orange-100"
                                      >
                                        {isValidImgSrc(
                                          amenity?.FacilityImage,
                                        ) && (
                                          <Image
                                            width={13}
                                            height={13}
                                            src={amenity.FacilityImage}
                                            alt={
                                              amenity?.Name ||
                                              `Hotel Facility ${i + 1}`
                                            }
                                          />
                                        )}
                                        <span>{amenity?.Name}</span>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            )}

                          {/* Price & CTA */}
                          <div className="mt-auto pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between flex-wrap">
                              {/* <div>
                            <div className="text-sm text-gray-600">
                              Starting from
                            </div>
                            <div className="flex items-center gap-1">
                              <IndianRupee className="w-5 h-5 text-orange-600" />
                              <span className="text-2xl font-bold text-gray-900">
                                {hotel.price}
                              </span>
                              <span className="text-gray-600 text-sm">
                                / night
                              </span>
                            </div>
                          </div> */}
                              <div className="flex items-center gap-2 text-orange-600 font-semibold group-hover:gap-3 transition-all">
                                View Details
                                <ArrowRight className="w-5 h-5" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
